import APIError from '../errors/APIError';
import HttpClient from './utils/HttpClient';

export interface IMelhorEnvioOrder {
  id: string;
  service_id: number;
  quote: number;
  price: number;
  discount: number;
  status: string;
  tracking: string | null;
  self_tracking: string | null;
  from: {
    name: string;
    phone: string;
    email: string;
  };
  to: {
    name: string;
    phone: string;
    email: string;
  };
  service: {
    id: 1;
    name: string;
    type: string;
    range: string;
    company: {
      id: 1;
      name: string;
      picture: string;
    }
  };
  agency: string | null;
  tags: {
    tag: string;
    url: string | null;
  }[];
  products: {
    name: string;
    quantity: number;
    unitary_value: number;
  }[];
  volumes: {
    id: number,
    height: string,
    width: string,
    length: string,
    diameter: string,
    weight: string,
    format: string,
    created_at: string,
    updated_at: string
  }[];
}

interface IListTagsRes {
  current_page: number,
	data: IMelhorEnvioOrder[],
	first_page_url: string,
	from: number,
	last_page: number,
	last_page_url: string,
	next_page_url: string,
	path: string,
	per_page: number,
	prev_page_url: string | null,
	to: number,
	total: number
}

interface IBalanceRes {
  balance: number;
	reserved: number;
	debts: number;
}

export interface IAgency {
  id: number,
  name: string,
  initials: string,
  code: string,
  company_name: string,
  status: string,
  email: string,
  note: null,
  company_id: number,
  address: {
    id: number,
    label: string,
    postal_code: string,
    address: string,
    number: number | null,
    complement: string | null,
    district: string,
    latitude: number,
    longitude: number,
    confirmed_at: string | null,
    created_at: string,
    updated_at: string,
    city: {
      id: number,
      city: string,
      state: {
        id: number,
        state: string,
        state_abbr: string,
        country: {
          id: string,
          country: string
        }
      }
    }
  },
  phone: {
    id: number,
    label: string,
    phone: string,
    type: string,
    country_id: string,
    confirmed_at: string | null,
    created_at: string,
    updated_at: string
  }
}

const baseEndpoint = '/melhor_envio_test';

class MelhorEnvioService {
  private httpClient = HttpClient;

  public async getServices(): Promise<any> {
    try {
      const { data } = await this.httpClient.get('melhor_envio/services/list');

      return data;

    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar listar os serviços';
      }

      throw new APIError(errorMessage);
    }
  }

  public async insertCart(
      agency: number | null,
      cnaeCode: string,
      invoiceKey: string,
      orderId: number, 
      companyId: number
    ): Promise<any> {

    const { data } = await this.httpClient.post(
      `/companies/${companyId}${baseEndpoint}/cart/${orderId}`,
      {
        agency,
        cnaeCode,
        invoiceKey,
      }
    );

    return data;
  }

  public async buyTags(orders: any[], companyId: number): Promise<any> {
    const { data } = await this.httpClient.post(
      `/companies/${companyId}${baseEndpoint}/tag/buy`,
      {orders}
    );

    return data;
  }

  public async generateTags(orders: any[], companyId: number): Promise<any> {
    const { data } = await this.httpClient.post(
      `/companies/${companyId}${baseEndpoint}/tag/generate`,
      {orders}
    );

    return data;
  }

  public async pritnTags(orders: any[], companyId: number): Promise<any> {
    const { data } = await this.httpClient.post(
      `/companies/${companyId}${baseEndpoint}/tag/print`,
      {orders}
    );

    return data;
  }

  public async showTags(status: string, companyId: number): Promise<IListTagsRes> {
    const statusParam = status ? `?status=${status}` : '';

    const { data } = await this.httpClient.get(`/companies/${companyId}${baseEndpoint}/tag/list`+statusParam);

    return data;
  }

  public async findTag(orderId: string, companyId: number): Promise<IMelhorEnvioOrder> {
    const { data } = await this.httpClient.get(`/companies/${companyId}${baseEndpoint}/tag/search?orderId=${orderId}`);

    return data;
  }

  public async trackTags(orders: any[], companyId: number): Promise<any> {
    const { data } = await this.httpClient.post(
      `/companies/${companyId}${baseEndpoint}/tag/tracking`,
      {orders}
    );

    return data;
  }

  public async cancelTag(id: string, description: string, companyId: number): Promise<any> {
    const { data } = await this.httpClient.post(
      `/companies/${companyId}${baseEndpoint}/tag/cancel`,
      {
        id,
        description
      }
    );

    return data;
  }

  public async showBalance(companyId: number): Promise<IBalanceRes> {
    const { data } = await this.httpClient.get(`/companies/${companyId}${baseEndpoint}/user/balance`);

    return data;
  }

  public async addBalance(gateway: string, value: string, companyId: number): Promise<any> {
    const { data } = await this.httpClient.post(
      `/companies/${companyId}${baseEndpoint}/user/balance`,
      {
        gateway,
        value
      }
    );

    return data;
  }

  public async findAgencies(state: string, city: string, companyId: number): Promise<IAgency[]> {
    const stateParam = `?state=${state}`;
    const cityParam = `&city=${city}`;

    const { data } = await this.httpClient.get(`/companies/${companyId}${baseEndpoint}/agency${stateParam}${cityParam}`);

    return data;
  }
}

export default new MelhorEnvioService();
